import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import RegisterTab from '../components/RegisterTab';
import ScheduleVisitDialog from '../components/ScheduleVisitDialog';
import { OutcomeChip, VisitStatusChip } from '../components/VisitChips';
import {
  EngineerOption,
  ProjectOption,
  projectLabel,
  siteVisitsApi,
  STATUS_LABELS,
  VISIT_BY_LABELS,
  VisitFilters,
  VisitStatus,
  VisitSummary,
} from '../siteVisits.api';
import { useSiteVisitMeta } from '../useSiteVisitMeta';

export const siteVisitPath = (id: number) => `/work/site-visits/${id}`;

/**
 * Site visits for the office: every visit, filtered by status, engineer, project and dates,
 * with "Schedule visit"; and the Register tab — visits per project against the quotation's
 * free visits, and per engineer.
 */
export default function SiteVisitsPage() {
  const navigate = useNavigate();
  const meta = useSiteVisitMeta();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'register' ? 1 : 0;

  const [filters, setFilters] = useState<VisitFilters>({ status: '', engineerId: '', jobId: '', from: '', to: '' });
  const [rows, setRows] = useState<VisitSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    siteVisitsApi
      .list(filters)
      .then(setRows)
      .catch((e) => setError(logError({ message: 'Could not load site visits', error: e, code: 'SITE_VISITS_LIST_FAILED' })))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    if (tab === 0) load();
  }, [tab, load]);

  const set = (patch: Partial<VisitFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const project = meta?.projects.find((p) => p.id === filters.jobId) ?? null;
  const engineer = meta?.engineers.find((e) => e.id === filters.engineerId) ?? null;

  return (
    <Stack gap={3}>
      <SectionTitle
        title='SITE VISITS'
        action={
          <Stack direction='row' gap={1}>
            <Button variant='outlined' startIcon={<Icon icon='mdi:format-list-checks' />} onClick={() => navigate('/work/site-visits/checklists')}>
              Checklists
            </Button>
            <Button variant='contained' startIcon={<Icon icon='mdi:plus' />} onClick={() => setScheduling(true)}>
              Schedule visit
            </Button>
          </Stack>
        }
      />
      <Tabs value={tab} onChange={(_, v) => setParams(v === 1 ? { tab: 'register' } : {}, { replace: true })}>
        <Tab label='Visits' />
        <Tab label='Register' />
      </Tabs>

      {tab === 1 ? (
        <RegisterTab />
      ) : (
        <>
          <Card sx={{ py: 2 }}>
            <Stack direction='row' gap={2} flexWrap='wrap' alignItems='center'>
              <TextField select size='small' label='Status' sx={{ minWidth: 150 }} value={filters.status}
                onChange={(e) => set({ status: e.target.value as VisitStatus | '' })}>
                <MenuItem value=''>All</MenuItem>
                {(Object.keys(STATUS_LABELS) as VisitStatus[]).map((s) => (
                  <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                ))}
              </TextField>
              <Autocomplete<EngineerOption>
                size='small'
                sx={{ minWidth: 220 }}
                options={meta?.engineers ?? []}
                value={engineer}
                onChange={(_, e) => set({ engineerId: e?.id ?? '' })}
                getOptionLabel={(e) => e.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(p) => <TextField {...p} label='Engineer' />}
              />
              <Autocomplete<ProjectOption>
                size='small'
                sx={{ minWidth: 300 }}
                options={meta?.projects ?? []}
                value={project}
                onChange={(_, p) => set({ jobId: p?.id ?? '' })}
                getOptionLabel={(p) => projectLabel(p.code, p.name, p.location)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(p) => <TextField {...p} label='Project' placeholder='Search code, name or location' />}
              />
              <TextField label='From' type='date' size='small' value={filters.from} onChange={(e) => set({ from: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <TextField label='To' type='date' size='small' value={filters.to} onChange={(e) => set({ to: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Stack>
          </Card>
          {error && <Alert severity='error'>{error}</Alert>}
          <Card sx={{ p: 0 }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Visit no.</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Engineer</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((v) => (
                    <TableRow key={v.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(siteVisitPath(v.id))}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{v.visitNo}</TableCell>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>{projectLabel(v.projectCode, v.projectName, v.projectLocation)}</Typography>
                        {v.stages && <Typography variant='caption' color='text.secondary'>{v.stages}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{v.engineerName ?? '—'}</Typography>
                        {v.visitBy === 'DIRECTOR' && (
                          <Typography variant='caption' color='text.secondary'>{VISIT_BY_LABELS.DIRECTOR}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant='body2'>{dayjs(v.scheduledDate).format('DD MMM YYYY')}</Typography>
                        <Typography variant='caption' color='text.secondary'>{v.shiftLabel ?? v.shift}</Typography>
                      </TableCell>
                      <TableCell>{v.purpose ?? '—'}</TableCell>
                      <TableCell><VisitStatusChip status={v.status} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant='body2'>{v.checksDone}/{v.checksTotal} checks</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          <Typography component='span' variant='caption' sx={v.issues ? { color: 'error.main', fontWeight: 700 } : undefined}>
                            {v.issues} issue{v.issues === 1 ? '' : 's'}
                          </Typography>
                          {' · '}{v.photoCount} photo{v.photoCount === 1 ? '' : 's'}
                        </Typography>
                      </TableCell>
                      <TableCell><OutcomeChip outcome={v.outcome} label={v.outcomeLabel} /></TableCell>
                    </TableRow>
                  ))}
                  {!loading && !rows.length && (
                    <TableRow>
                      <TableCell {...({ colSpan: 8 } as object)} align='center' sx={{ py: 4, color: 'text.secondary' }}>
                        No site visits match. Use “Schedule visit” to plan one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      <ScheduleVisitDialog
        open={scheduling}
        meta={meta}
        visit={null}
        onClose={() => setScheduling(false)}
        onSaved={(v) => {
          setScheduling(false);
          navigate(siteVisitPath(v.id));
        }}
      />
    </Stack>
  );
}
